import { useState } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { useT } from "@/i18n";
import { CheckCircle2, Circle, Star, Pencil, Plus, X } from "lucide-react";

// Preset perks — identical taxonomy to the mobile Inner-Circle builder.
// Core perks (core:true) power the AI-assisted workflow and render first.
// `label`/`unit` are the canonical (German) values persisted to the backend;
// `labelKey`/`unitKey` drive the localized UI display only.
export const PRESET_PERKS = [
  { id: "weva_snapshot_analysis", label: "WEVA Snapshot-Analysen", kind: "content", hasQty: true, core: true, unit: "/Monat", unitKey: "perk.unit_month" },
  { id: "user_entries_analysis", label: "Eintrags-Analysen", kind: "chat", hasQty: true, core: true, unit: "/Monat", unitKey: "perk.unit_month" },
  { id: "weekly_plan", label: "Wochenplan vom Coach", kind: "content", hasQty: true, core: true, unit: "/Monat", unitKey: "perk.unit_month" },
  { id: "dm_monthly", label: "Direktnachrichten pro Monat", kind: "chat", hasQty: true, core: true, unit: "DMs/Monat", unitKey: "perk.unit_dms_month" },
  { id: "paid_content", label: "Zugang zu allen bezahlten Inhalten", kind: "content" },
  { id: "group_chat", label: "Privater Gruppenchat mit anderen Mitgliedern", kind: "group" },
  { id: "video_call", label: "1:1 Videocall-Minuten pro Monat", kind: "video", hasQty: true, unit: "Min./Monat", unitKey: "perk.unit_min_month" },
  { id: "belief_text_beliefs", label: "Glaubenssatz-Arbeit per Text · Glaubenssätze pro Monat", kind: "chat", hasQty: true, unit: "/Monat", unitKey: "perk.unit_month" },
  { id: "belief_text_dms", label: "Glaubenssatz-Arbeit per Text · DMs pro Monat", kind: "chat", hasQty: true, unit: "DMs/Monat", unitKey: "perk.unit_dms_month" },
  { id: "belief_phone", label: "Glaubenssatz-Arbeit per Telefon · Minuten pro Monat", kind: "phone", hasQty: true, unit: "Min./Monat", unitKey: "perk.unit_min_month" },
  { id: "belief_video", label: "Glaubenssatz-Arbeit per Videocall · Minuten pro Monat", kind: "video", hasQty: true, unit: "Min./Monat", unitKey: "perk.unit_min_month" },
  { id: "emergency_support", label: "Emotionale Notfall-Unterstützung", kind: "support" },
];
export const CORE_DEFAULTS = { weva_snapshot_analysis: 4, user_entries_analysis: 8, weekly_plan: 4, dm_monthly: 20 };

// Persisted perks array (backend shape) → controlled editor value.
export function perksToValue(perksArray) {
  const selected = {};
  const custom = [];
  for (const p of perksArray || []) {
    const match = PRESET_PERKS.find((pp) => pp.id === p.id);
    if (match) selected[p.id] = { qty: p.qty ?? undefined, note: p.note ?? undefined };
    else custom.push(p);
  }
  return { selected, custom };
}

// Controlled editor value → backend perks array.
export function valueToPerks(value) {
  return [
    ...Object.entries(value?.selected || {}).map(([id, val]) => {
      const preset = PRESET_PERKS.find((p) => p.id === id);
      return { id, label: preset?.label || id, kind: preset?.kind || "custom", qty: val.qty, note: val.note };
    }),
    ...((value?.custom) || []),
  ];
}

// Controlled perks editor: preset checklist with quantities + custom offerings.
// value = { selected: { [id]: {qty, note} }, custom: [{id,label,kind,qty,duration_min}] }
export default function PerksEditor({ value, onChange }) {
  const t = useT();
  const selected = value?.selected || {};
  const custom = value?.custom || [];
  const [customLabel, setCustomLabel] = useState("");
  const [customQty, setCustomQty] = useState("");
  const [customDuration, setCustomDuration] = useState("");

  const togglePreset = (p) => {
    const next = { ...selected };
    if (next[p.id]) delete next[p.id];
    else next[p.id] = { qty: p.hasQty ? (CORE_DEFAULTS[p.id] ?? 5) : undefined };
    onChange({ selected: next, custom });
  };
  const setQty = (id, qty) => onChange({ selected: { ...selected, [id]: { ...selected[id], qty } }, custom });

  const addCustom = () => {
    if (!customLabel.trim()) return;
    onChange({
      selected,
      custom: [...custom, { id: `custom_${Date.now()}`, label: customLabel.trim(), kind: "custom", qty: parseInt(customQty) || undefined, duration_min: parseInt(customDuration) || undefined }],
    });
    setCustomLabel(""); setCustomQty(""); setCustomDuration("");
  };
  const editCustom = (p) => {
    setCustomLabel(p.label || "");
    setCustomQty(p.qty ? String(p.qty) : "");
    setCustomDuration(p.duration_min ? String(p.duration_min) : "");
    onChange({ selected, custom: custom.filter((x) => x.id !== p.id) });
  };
  const removeCustom = (id) => onChange({ selected, custom: custom.filter((p) => p.id !== id) });

  return (
    <div className="space-y-4">
      <div className="space-y-2">
        {PRESET_PERKS.map((p) => {
          const on = !!selected[p.id];
          return (
            <div key={p.id} className={`flex items-center gap-3 rounded-xl border p-3 ${on ? "border-primary/40 bg-primary/5" : p.core ? "border-dashed border-primary/40 bg-card" : "border-border bg-card"}`}>
              <button type="button" onClick={() => togglePreset(p)} data-testid={`perk-${p.id}`} className="flex flex-1 items-center gap-2 text-left">
                {on ? <CheckCircle2 className="h-5 w-5 shrink-0 text-primary" /> : <Circle className={`h-5 w-5 shrink-0 ${p.core ? "text-primary" : "text-muted-foreground"}`} />}
                <span className="text-sm text-foreground">
                  {t(`perk.${p.id}`)}
                  {p.core && !on && <span className="ml-1 text-xs text-primary">{t("perk.recommended")}</span>}
                </span>
              </button>
              {on && p.hasQty && (
                <div className="flex items-center gap-1.5">
                  <Input data-testid={`perk-qty-${p.id}`} className="h-8 w-16 text-center" inputMode="numeric" value={String(selected[p.id]?.qty ?? "")} onChange={(e) => setQty(p.id, parseInt(e.target.value) || 0)} />
                  <span className="text-xs text-muted-foreground">{t(p.unitKey)}</span>
                </div>
              )}
            </div>
          );
        })}
      </div>

      <div>
        <p className="mb-2 text-xs text-muted-foreground">{t("perk.custom_hint")}</p>
        <div className="space-y-2">
          {custom.map((p) => (
            <div key={p.id} className="flex items-center gap-3 rounded-xl border border-primary/40 bg-primary/5 p-3">
              <button type="button" onClick={() => editCustom(p)} data-testid={`custom-edit-${p.id}`} className="flex flex-1 items-center gap-2 text-left">
                <Star className="h-4 w-4 text-primary" />
                <span className="text-sm text-foreground">
                  {p.label}{p.qty ? ` · ${p.qty}×` : ""}{p.duration_min ? ` · ${p.duration_min} ${t("perk.min_short")}` : ""}
                </span>
                <Pencil className="h-3.5 w-3.5 text-muted-foreground" />
              </button>
              <button type="button" onClick={() => removeCustom(p.id)} data-testid={`custom-remove-${p.id}`} className="text-muted-foreground hover:text-foreground">
                <X className="h-4 w-4" />
              </button>
            </div>
          ))}
        </div>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Input data-testid="custom-perk-label" className="min-w-[180px] flex-1" value={customLabel} placeholder={t("perk.offer_name_ph")} onChange={(e) => setCustomLabel(e.target.value)} />
          <Input data-testid="custom-perk-qty" className="w-20" inputMode="numeric" value={customQty} placeholder={t("perk.count_ph")} onChange={(e) => setCustomQty(e.target.value)} />
          <Input data-testid="custom-perk-duration" className="w-20" inputMode="numeric" value={customDuration} placeholder={t("perk.min_short")} onChange={(e) => setCustomDuration(e.target.value)} />
          <Button type="button" variant="outline" className="gap-1 rounded-full" onClick={addCustom} data-testid="custom-perk-add"><Plus className="h-4 w-4" /> {t("perk.add")}</Button>
        </div>
      </div>
    </div>
  );
}
